import { ChangeEvent, memo, ReactNode, useCallback, useMemo, useState } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { ConfigListPageShell } from '@mobileComponents/ConfigListPageShell'
import {
  MOBILE_REPORTS_ARCHIVE_COLUMNS,
  MOBILE_REPORTS_COLUMNS,
  REPORTS_PAGE_SIZE,
  REPORTS_TTL_DAYS_FALLBACK,
  formatReportDeletesAt,
} from '@mobileConstants'
import { buildReportsRsql } from '@mobileHelpers'
import {
  useArchiveMobileReports,
  useMobileReports,
  useRestoreMobileReports,
} from '@mobileHooks'
import { EMPTY_REPORTS_FILTERS, IncidentReport, ReportsFilters } from '@mobileTypes'
import { Archive as ArchiveIcon, Unarchive as UnarchiveIcon } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from '@mui/material'

import { generateDataGridActionsColumn } from '@helpers'
import { ROUTES } from '@constants'

const ReportsFiltersBar = memo(
  ({
    filters,
    onChange,
    onApply,
    onReset,
    action,
  }: {
    filters: ReportsFilters
    onChange: (next: ReportsFilters) => void
    onApply: () => void
    onReset: () => void
    action?: ReactNode
  }) => {
    const handleField = (field: keyof ReportsFilters) => (event: ChangeEvent<HTMLInputElement>) => {
      onChange({ ...filters, [field]: event.target.value })
    }

    return (
      <Box
        component='form'
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
          alignItems: 'center',
        }}
        onSubmit={event => {
          event.preventDefault()
          onApply()
        }}
      >
        <FormControl size='small' sx={{ minWidth: 110 }}>
          <InputLabel id='reports-os-label'>OS</InputLabel>
          <Select
            label='OS'
            labelId='reports-os-label'
            value={filters.os}
            onChange={event =>
              onChange({ ...filters, os: event.target.value as ReportsFilters['os'] })
            }
          >
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='android'>Android</MenuItem>
            <MenuItem value='ios'>iOS</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label='Config ID'
          size='small'
          value={filters.configId}
          onChange={handleField('configId')}
        />
        <TextField
          label='Trigger'
          size='small'
          value={filters.triggerKind}
          onChange={handleField('triggerKind')}
        />
        <TextField
          label='Message'
          size='small'
          value={filters.message}
          onChange={handleField('message')}
        />
        <TextField
          InputLabelProps={{ shrink: true }}
          label='From'
          size='small'
          type='datetime-local'
          value={filters.from}
          onChange={handleField('from')}
        />
        <TextField
          InputLabelProps={{ shrink: true }}
          label='To'
          size='small'
          type='datetime-local'
          value={filters.to}
          onChange={handleField('to')}
        />
        <Button type='submit' variant='contained'>
          Apply
        </Button>
        <Button type='button' variant='outlined' onClick={onReset}>
          Reset
        </Button>
        {action}
      </Box>
    )
  }
)

ReportsFiltersBar.displayName = 'ReportsFiltersBar'

function selectionIds(model: unknown): string[] {
  if (Array.isArray(model)) {
    return model.map(String)
  }
  if (model && typeof model === 'object' && 'ids' in model) {
    return [...((model as { ids: Iterable<unknown> }).ids ?? [])].map(String)
  }
  return []
}

export const ConfigMobileReports = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isAllReports = matchPath(ROUTES.CONFIG_MOBILE_REPORTS, pathname)

  const [draftFilters, setDraftFilters] = useState<ReportsFilters>(EMPTY_REPORTS_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState<ReportsFilters>(EMPTY_REPORTS_FILTERS)
  const [page, setPage] = useState(0)
  const [archivedView, setArchivedView] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const query = useMemo(() => buildReportsRsql(appliedFilters), [appliedFilters])
  const { items, total, ttlDays, loading, isError, refetch } = useMobileReports({
    query,
    limit: REPORTS_PAGE_SIZE,
    offset: page * REPORTS_PAGE_SIZE,
    archived: archivedView,
  })
  const [archiveReports, { isLoading: archiving }] = useArchiveMobileReports()
  const [restoreReports, { isLoading: restoring }] = useRestoreMobileReports()
  const retentionDays = ttlDays ?? REPORTS_TTL_DAYS_FALLBACK

  const handleOpen = useCallback(
    (incidentId: string) => {
      const suffix = archivedView ? '?archived=true' : ''
      navigate(`${ROUTES.CONFIG_MOBILE_REPORTS}/${incidentId}${suffix}`)
    },
    [archivedView, navigate]
  )

  const handleApply = useCallback(() => {
    setAppliedFilters(draftFilters)
    if (page == 0) {
      refetch()
    } else {
      setPage(0)
    }
  }, [draftFilters, page, refetch])

  const handleReset = useCallback(() => {
    setDraftFilters(EMPTY_REPORTS_FILTERS)
    setAppliedFilters(EMPTY_REPORTS_FILTERS)
    setPage(0)
  }, [])

  const handleTabChange = useCallback((_: unknown, value: number) => {
    setArchivedView(value === 1)
    setSelectedIds([])
    setPage(0)
  }, [])

  const handleMove = useCallback(async () => {
    if (selectedIds.length === 0) {
      return
    }
    if (archivedView) {
      await restoreReports({ incidentIds: selectedIds }).unwrap()
    } else {
      await archiveReports({ incidentIds: selectedIds }).unwrap()
    }
    setSelectedIds([])
  }, [archiveReports, archivedView, restoreReports, selectedIds])

  const columns = useMemo(
    () => [
      ...MOBILE_REPORTS_COLUMNS,
      ...(archivedView
        ? [
            ...MOBILE_REPORTS_ARCHIVE_COLUMNS,
            {
              field: 'deletesAt',
              headerName: 'Deletes at',
              width: 180,
              renderCell: (params: { row: IncidentReport }) => {
                const archivedAt = params.row.archivedAt

                return archivedAt ? formatReportDeletesAt(archivedAt, retentionDays) : '—'
              },
            },
          ]
        : [])
    ],
    [archivedView, retentionDays]
  )

  const pageCount = Math.max(1, Math.ceil(total / REPORTS_PAGE_SIZE))
  const busy = archiving || restoring

  return (
    <>
      <Outlet />

      <ConfigListPageShell title='Mobile Reports' isVisible={!!isAllReports}>
        <Tabs sx={{ mb: 2 }} value={archivedView ? 1 : 0} onChange={handleTabChange}>
          <Tab label='Active' />
          <Tab label='Archive' />
        </Tabs>
        {archivedView && <Alert severity='info' sx={{ mb: 2 }}>
          {`Archived reports are deleted automatically after ${retentionDays} days. Restore them to keep.`}
        </Alert>}
        <ReportsFiltersBar
          action={
            <Button
              disabled={selectedIds.length === 0 || busy}
              startIcon={archivedView ? <UnarchiveIcon /> : <ArchiveIcon />}
              variant='contained'
              onClick={() => void handleMove()}
            >
              {archivedView
                ? `Restore selected (${selectedIds.length})`
                : `Archive selected (${selectedIds.length})`}
            </Button>
          }
          filters={draftFilters}
          onApply={handleApply}
          onChange={setDraftFilters}
          onReset={handleReset}
        />
        {isError && (
          <Alert severity='error' sx={{ mb: 2 }}>
            Failed to load reports. Check that wiskey-mobile-reports is running.
          </Alert>
        )}
        <Box sx={{ display: 'flex', flex: 1, minHeight: 400 }}>
          <DataGrid
            checkboxSelection
            columns={columns}
            disableRowSelectionOnClick
            getRowId={row => (row as IncidentReport).incidentId}
            loading={loading}
            pageCount={pageCount}
            pagination
            paginationMode='server'
            paginationModel={{ page, pageSize: REPORTS_PAGE_SIZE }}
            rowCount={total}
            rowHoverCursor='pointer'
            rowSelectionModel={selectedIds}
            rows={items}
            onChangePage={(_event, value) => setPage(value - 1)}
            onPaginationModelChange={model => setPage(model.page)}
            onRowClick={params => handleOpen((params.row as IncidentReport).incidentId)}
            onRowSelectionModelChange={model => setSelectedIds(selectionIds(model))}
          />
        </Box>
      </ConfigListPageShell>
    </>
  )
}
